// #region Import

/* Telestream */
import {
  EXECUTE_PLUGIN,
  FETCH_PLUGIN_SCHEMAS,
  FETCH_CLIPLIST_PLUGIN_SCHEMAS,
  MARK_IN,
  MARK_OUT,
  UPDATE_PLUGIN_MODEL,
  UPDATE_PLUGIN_SETTINGS,
  RESET_VIDEO_STATE,
} from '@/utils/action-types';
import {
  SET_PLUGIN_MODEL,
  SET_PLUGIN_SCHEMA,
  SET_PLUGIN_SCHEMAS,
  SET_PLUGINS_CONNECTION,
  SET_PLUGIN_SELECTION
} from '@/utils/mutation-types';
import { ConnectionService } from '@/services/connection.service';
import { getSharableVideoLinkQueryString } from './utilities/plugins.utils';
import { sendEphemeralSnack } from './utilities/snacks.utils';
import {
  formatMarker,
  populateClipListMediaDefaults
} from './utilities/schema.utils';

// #endregion Imports

export const plugins = {
  // #region State

  state: {
    connection: undefined,
    schemas:    [],
    selection:  0
  },

  // #endregion State

  // #region Mutations

  mutations: {
    [SET_PLUGIN_SCHEMAS](state, schemas) {
      state.schemas = schemas;
    },
    [SET_PLUGINS_CONNECTION](state, connection) {
      state.connection = connection;
    },
    [SET_PLUGIN_MODEL](state, { schema, model }) {
      const schemaToUpdate = state.schemas.find((s) => s.name === schema.name);
      if (!schemaToUpdate) {
        return;
      }

      schemaToUpdate.model = model;
    },
    [SET_PLUGIN_SCHEMA](state, { schema, schemaSchema }) {
      const schemaToUpdate = state.schemas.find((s) => s.name === schema.name);
      if (!schemaToUpdate) {
        return;
      }

      schemaToUpdate.schema = schemaSchema;
    },
    [SET_PLUGIN_SELECTION](state, selection) {
      state.selection = selection;
    },
  },

  // #endregion Mutations

  // #region Actions

  actions: {
    async [FETCH_PLUGIN_SCHEMAS](context, origin) {
      if (context.state.connection) {
        context.state.connection.stop();
      }

      const connection = ConnectionService.getHubConnection(origin, '/plugin', true);
      await connection.start();

      connection.on('Alert', (alert, level) => onPluginAlert(context, level, alert));
      connection.on('Unload', () => onPluginUnload(context));

      context.commit(SET_PLUGINS_CONNECTION, connection);

      const schemas = await connection.invoke('GetPluginSchemas');
      context.commit(
        SET_PLUGIN_SCHEMAS,
        schemas.map((s) => JSON.parse(s))
      );

      // Initialize the plug-in data models:
      context.state.schemas.forEach(async (schema) => {
        const initializedModel = await connection.invoke(
          'Initialize',
          schema.name,
          JSON.stringify(schema.model),
          context.rootState.videoPath,
          ConnectionService.getUrl(''),
          getSharableVideoLinkQueryString(context.rootState.videoController, context.rootState.currentSidecarAudioPaths)
        );

        if (initializedModel) {
          context.commit(SET_PLUGIN_MODEL, { schema: schema, model: JSON.parse(initializedModel) });
        }
      });

      context.dispatch(FETCH_CLIPLIST_PLUGIN_SCHEMAS);
    },

    async [UPDATE_PLUGIN_MODEL](context, schema) {
      const serializedModel = await context.state.connection.invoke(
        'Update',
        schema.name,
        JSON.stringify(schema.model),
        context.rootState.videoPath,
        ConnectionService.getUrl(''),
        getSharableVideoLinkQueryString(context.rootState.videoController, context.rootState.currentSidecarAudioPaths),
        context.rootState.videoOpen ? context.rootState.videoOpen : false
      );

      const model = JSON.parse(serializedModel);
      if (model) {
        context.commit(SET_PLUGIN_MODEL, { schema: schema, model: model });

        const updatedSchema = populateClipListMediaDefaults(context, model, schema.schema);
        context.commit(SET_PLUGIN_SCHEMA, { schema: schema, schemaSchema: updatedSchema });
      }
    },

    async [UPDATE_PLUGIN_SETTINGS](context, { pluginName, settingsModel }) {
      await context.state.connection.invoke(
        'UpdateSettings',
        pluginName,
        JSON.stringify(settingsModel)
      );
    },

    async [EXECUTE_PLUGIN](context, schema) {
      const schemaToExecute = schema || context.state.schemas[context.state.selection];
      if (!schemaToExecute) {
        return;
      }

      let result = await context.state.connection.invoke(
        'Execute',
        schemaToExecute.name,
        JSON.stringify(schemaToExecute.model),
        context.rootState.videoPath,
        ConnectionService.getUrl(''),
        getSharableVideoLinkQueryString(context.rootState.videoController, context.rootState.currentSidecarAudioPaths),
        context.rootState.videoOpen ? context.rootState.videoOpen : false
      );

      if (!result) {
        return;
      }

      const updatedModel = JSON.parse(result);
      context.commit(SET_PLUGIN_MODEL, { schema: schemaToExecute, model: updatedModel });

      if (!updatedModel.download) {
        return;
      }

      const link = document.createElement('a');
      link.download = 'ImageLink';
      link.href = updatedModel.download;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    [MARK_IN](context) {
      const selectedSchema = context.state.schemas[context.state.selection];
      if (!selectedSchema) return;
      const model = { ...selectedSchema.model };
      const marker = formatMarker(context.rootState.video.currentTimecode, context.rootState.video.currentFrame);

      if (selectedSchema.schema.properties.markin) {
        model.markin = marker;
      }

      if (selectedSchema.schema.properties.startFrame) {
        model.startFrame = marker;
      }

      context.commit(SET_PLUGIN_MODEL, {
        schema: selectedSchema,
        model:  model,
      });
    },

    [MARK_OUT](context) {
      const selectedSchema = context.state.schemas[context.state.selection];
      if (!selectedSchema) return;
      const marker = formatMarker(context.rootState.video.currentTimecode, context.rootState.video.currentFrame);

      if (selectedSchema.schema.properties.markout) {
        context.commit(SET_PLUGIN_MODEL, {
          schema: selectedSchema,
          model:  {
            ...selectedSchema.model,
            markout: marker
          },
        });
      }
    },
  },

  // #endregion Actions
};

// #region Utilities

function onPluginAlert(context, level, alert) {
  sendEphemeralSnack(context, level, alert);
}

function onPluginUnload(context) {
  context.dispatch(RESET_VIDEO_STATE);
}

// #endregion Utilities
